/**
 * Sound Management Types
 * Types for managing sound files in translation system
 */

import { ApplicationType, MediaType } from './translation-types';

// ============================================================================
// Core Interfaces
// ============================================================================

/**
 * Represents a media item (sound file) from the API
 */
export interface MediaItem {
  mediaId: number;
  applicationType: ApplicationType;
  mediaType: MediaType;
  languageCode: string;
  keyName: string | null;
  fileName: string;
  originalFileName: string;
  s3Url: string;
  contentType: string;
  fileSizeBytes: number;
  created: string;
  createdByName: string | null;
}

/**
 * Common sound key names used in charger/kiosk applications
 */
export const COMMON_SOUND_KEYS = [
  'authorizing',
  'card_approved',
  'card_not_approved',
  'present_your_card',
  'scan_the_qr',
  'thank_you',
  'transaction_canceled',
  'use_the_same_card',
] as const;

export type CommonSoundKey = typeof COMMON_SOUND_KEYS[number];

// ============================================================================
// Request Types
// ============================================================================

/**
 * Parameters for listing media/sounds
 */
export interface MediaListParams {
  applicationType: ApplicationType;
  mediaType: MediaType;
  languageCode?: string;
  skip?: number;
  take?: number;
}

/**
 * Request to upload a sound file
 */
export interface UploadSoundRequest {
  file: File;
  applicationType: ApplicationType;
  mediaType: MediaType; // Should be MediaType.Sound (4)
  languageCode: string;
  keyName?: string;
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response for media list operations
 */
export interface MediaListResponse {
  isSuccess: boolean;
  rows: MediaItem[];
  count: number;
  errorMessage?: string;
}

/**
 * Response for single media operations (upload, get by id)
 */
export interface MediaResponse {
  isSuccess: boolean;
  media?: MediaItem;
  errorMessage?: string;
}

/**
 * Response for delete media operation
 */
export interface DeleteMediaResponse {
  isSuccess: boolean;
  errorMessage?: string;
}

/**
 * Result of copying sound files from a base language to a compound-coded
 * language (e.g. "he" -> "he#paz"), across all application types.
 */
export interface CopySoundsResult {
  isSuccess: boolean;
  message?: string;
  copiedCount: number;
  errorMessage?: string;
}

// ============================================================================
// Store Types
// ============================================================================

/**
 * Sound management store state and actions
 */
export interface SoundManagementStore {
  // Data
  sounds: MediaItem[];
  totalCount: number;
  currentPage: number;
  pageSize: number;

  // Filters
  applicationType: ApplicationType;
  languageFilter: string | null;

  // UI States
  isLoading: boolean;
  isUploading: boolean;
  isDeleting: boolean;
  isCopyingSounds: boolean;
  error: string | null;
  uploadError: string | null;
  copySoundsError: string | null;

  // Dialog state
  uploadDialogOpen: boolean;
  deleteDialogOpen: boolean;
  soundToDelete: MediaItem | null;
  copySoundsDialogOpen: boolean;

  // Audio playback
  playingMediaId: number | null;

  // Actions
  fetchSounds: () => Promise<void>;
  uploadSound: (request: UploadSoundRequest) => Promise<MediaItem>;
  deleteSound: (mediaId: number) => Promise<boolean>;
  copySoundsFromParent: (newLanguageCode: string, baseLanguageCode: string) => Promise<CopySoundsResult>;
  setApplicationType: (appType: ApplicationType) => void;
  setLanguageFilter: (languageCode: string | null) => void;
  setCurrentPage: (page: number) => void;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  openDeleteDialog: (sound: MediaItem) => void;
  closeDeleteDialog: () => void;
  openCopySoundsDialog: () => void;
  closeCopySoundsDialog: () => void;
  setPlayingMediaId: (mediaId: number | null) => void;
  clearUploadError: () => void;
  reset: () => void;
}

// ============================================================================
// Component Props Types
// ============================================================================

/**
 * Props for the SoundGrid component
 */
export interface SoundGridProps {
  sounds: MediaItem[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  playingMediaId: number | null;
  onPageChange: (page: number) => void;
  onPlay: (sound: MediaItem) => void;
  onStop: () => void;
  onDelete: (sound: MediaItem) => void;
  onRetry: () => void;
}

/**
 * Props for the UploadSoundDialog component
 */
export interface UploadSoundDialogProps {
  open: boolean;
  onClose: () => void;
  applicationType: ApplicationType;
  availableLanguages: { languageCode: string; languageName: string }[];
  onUpload: (request: UploadSoundRequest) => Promise<void>;
  isUploading: boolean;
  error: string | null;
}

// ============================================================================
// Helper Constants
// ============================================================================

/**
 * Supported audio file types for upload
 */
export const SUPPORTED_AUDIO_TYPES = [
  'audio/mpeg',      // .mp3
  'audio/wav',       // .wav
  'audio/ogg',       // .ogg
  'audio/x-wav',     // .wav (alternative MIME)
] as const;

/**
 * Supported audio file extensions
 */
export const SUPPORTED_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg'] as const;

/**
 * Maximum file size for audio uploads (5MB)
 */
export const MAX_AUDIO_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

/**
 * Default page size for sounds list
 */
export const DEFAULT_SOUNDS_PAGE_SIZE = 50;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Validate if file is a supported audio type
 */
export function isValidAudioFile(file: File): boolean {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  const isValidExtension = SUPPORTED_AUDIO_EXTENSIONS.includes(
    extension as typeof SUPPORTED_AUDIO_EXTENSIONS[number]
  );
  const isValidType = SUPPORTED_AUDIO_TYPES.includes(
    file.type as typeof SUPPORTED_AUDIO_TYPES[number]
  );
  return isValidExtension || isValidType;
}

/**
 * Validate file size
 */
export function isValidFileSize(file: File): boolean {
  return file.size <= MAX_AUDIO_FILE_SIZE;
}

/**
 * Generate suggested filename based on language code and sound key
 */
export function generateSoundFileName(languageCode: string, keyName: string): string {
  return `${languageCode.toLowerCase()}_${keyName.toLowerCase()}.mp3`;
}

/**
 * Application types that support sound media
 */
export const SOUND_SUPPORTED_APP_TYPES = [
  ApplicationType.EnergyAndroidCharger,
  ApplicationType.EnergyAndroidKiosk,
] as const;

/**
 * Check if application type supports sounds
 */
export function supportsSounds(appType: ApplicationType): boolean {
  return SOUND_SUPPORTED_APP_TYPES.includes(
    appType as typeof SOUND_SUPPORTED_APP_TYPES[number]
  );
}
